/*
  "Last update" on an asset — the date it last changed, for the Asset
  Management column of the same name.

  Set in the browser, because there is nowhere else it could be set. The client
  sends the whole UserData document to POST /user/data/update and the server
  stores it as given (Ostreon-Backend/Routes/User.js); there is no per-asset
  write hook, and every field the column would care about arrives as ciphertext,
  so the server cannot tell what changed even in principle.

  Set in ONE place — the save effect in Context/DataContext.js — rather than at
  each editor. Every change to an asset reaches that effect, including the
  indirect ones that no asset editor is involved in: deleting a beneficiary
  rewrites the beneficiary list of every asset they were on. Stamping at each
  edit site would mean finding all of them today and remembering every one
  added later, and a missed site does not fail loudly — the asset just reports
  a date that is quietly wrong.

  Stored as YYYY/MM/DD: the app-wide convention (Utils/isValidDateFormat.js),
  the same shape as evaluation_date and debt_payoff_date beside it, and it
  sorts correctly as plain text. Two edits on one day give the same value,
  which is what asking for a date rather than a time means.
*/

/*
  Not part of "the asset changed".

  The two order fields are the reader's arrangement of a table, not a fact
  about the asset — dragging a row up should not re-date it. `access` is owned
  by the server, which overwrites whatever the client sends. `__undecrypted`
  is the ciphertext pass-through bookkeeping from dataEncryption.js, and
  `last_updated` cannot be evidence of its own change.
*/
const IGNORED = new Set([
  "assets_order",
  "asset_management_order",
  "access",
  "last_updated",
  "__undecrypted",
]);

/*
  Key order is not stable across the rebuilds an asset goes through, so the
  keys are sorted before comparing — otherwise every asset looks changed after
  any round trip through encryptAsset/decryptAsset.

  Cheap despite stringifying the whole asset: `asset_files` holds only id, name,
  size and lastModified (decryptFiles), never the file's bytes.
*/
const fingerprint = (asset) => {
  if (!asset || typeof asset !== "object") return "";
  const keys = Object.keys(asset)
    .filter((key) => !IGNORED.has(key))
    .sort();
  return JSON.stringify(keys.map((key) => [key, asset[key]]));
};

export const todayStamp = (now = new Date()) => {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}/${month}/${day}`;
};

/*
  The assets with `last_updated` set on each one that differs from its
  baseline — or null when there is nothing to do.

  Null rather than an equal array on purpose. The caller writes the result back
  with setUserData, which re-runs the effect it was called from; returning null
  once every stamp is already correct is what stops that from looping. The
  second pass still sees a difference against the baseline, but the date it
  would write is the date already there, so it reports no work and the save
  goes ahead.

  An asset with no baseline has just been added, and is stamped: it has
  certainly changed since the server last saw it. An asset with no asset_id is
  left alone — there is no way to match it to a baseline, and guessing would
  re-date every asset on the account.
*/
export const stampUpdatedAssets = (assets, baseline, now) => {
  if (!Array.isArray(assets)) return null;

  const before = new Map();
  for (const asset of baseline || []) {
    if (asset && asset.asset_id) before.set(asset.asset_id, fingerprint(asset));
  }

  const stamp = todayStamp(now);
  let changed = false;
  const next = assets.map((asset) => {
    if (!asset || !asset.asset_id) return asset;
    const previous = before.get(asset.asset_id);
    const unchanged = previous !== undefined && previous === fingerprint(asset);
    if (unchanged || asset.last_updated === stamp) return asset;
    changed = true;
    return { ...asset, last_updated: stamp };
  });

  return changed ? next : null;
};
